/**
 *
 * Requirements:
 * - Emit Rental event with unitId, facilityId, and selected promoId
 * - Activate Unit Action Drawer in "rental mode"
 */
import React from "react";
import { Button } from "@/components/ui/button";
import { emitUnitEvent } from "@/utils/units/unitEvents";
import { useDisplayOptionContext } from "@/hooks/useDisplayOptionContext";
import { useUnitItemContext } from "@/hooks/useUnitItemContext";
import { useUnitJourneyContext } from "@/hooks/useUnitJourneyContext";
import { translateWithMultipleSources } from "@/lib/translate";
import { useFacilitiesContext } from "@/hooks/useFacilitiesContext";
import { FunnelType } from "@/lib/utils";


interface UnitRentButtonProps {
    unitId: number | string;
    tier?: string;
    onClick?: () => void;
}

let hasRunUseEffect = false;

export const UnitRentButton: React.FC<UnitRentButtonProps> = ({
    unitId,
    tier = "tierA",
    onClick
}) => {
    const { rentalApp, disableRental, language } = useDisplayOptionContext();
    const { unitGroup, selectedPromo } = useUnitItemContext();
    const journeyContext = useUnitJourneyContext();
    const { facilities } = useFacilitiesContext();

    const bgClass = tier === "tierB" ? "tw:bg-(--secondary)" : "tw:bg-(--primary)";

    React.useEffect(() => {
        const isEsign = window.location.href.includes('e-sign')
        if (!hasRunUseEffect && isEsign) {
            hasRunUseEffect = true;
            emitRentalEvent();
            activateRentalJourney();
        }
    }, []);

    // Emits the rental event
    const emitRentalEvent = () => {
        const facilityId = unitGroup.representative_unit.facility_id;
        emitUnitEvent(unitId, facilityId, selectedPromo, "rental");
    };

    // Handles external rental app redirection
    const handleExternalRental = () => {
        const facilityId = unitGroup.representative_unit.facility_id;
        const facilityInfo = facilities.find((facility) => facility.id === facilityId);

        if (facilityInfo && rentalApp?.externalUrl) {
            const translatedUrl = translateWithMultipleSources(rentalApp.externalUrl, {
                unitGroup,
                facility: facilityInfo,
                promo: { id: selectedPromo }
            }, Number(unitId), Number(selectedPromo));
            window.location.href = translatedUrl;
        }
    };

    // Opens the rental journey drawer
    const activateRentalJourney = () => {
        if (!journeyContext.isOpen) {
            journeyContext.setIsOpen(true);
        }
        journeyContext.setSelectedUnitId(unitId);

        // Here we need to set the journey to "vbp" if there are multiple sub-groups
        if (unitGroup.sub_groups && unitGroup.sub_groups.length > 1) {
            journeyContext.setUnitJourneyStep("vbp");
        } else {
            journeyContext.setUnitJourneyStep("funnel");
        }
        journeyContext.setFunnelType(FunnelType.RENT);
    };


    // Main handler for the rent button click
    const handleRentClick = () => {
        // Proceed with the onClick callback if provided
        if (onClick) {
            return onClick();
        }
        emitRentalEvent();

        if (rentalApp?.location === "external" && rentalApp.externalUrl) {
            handleExternalRental();
        } else {
            activateRentalJourney();
        }
    };

    // If rentals are disabled, do not render the button
    if (disableRental) return null;

    return (
        <Button variant="default" data-testid="rentButton" onClick={handleRentClick} className={`${bgClass} hover:${bgClass} tw:hover:brightness-75 rent-btn`}>
            { language?.action?.rentButton }
        </Button>
    );
};

UnitRentButton.displayName = "UnitRentButton";
